import { base, arbitrum } from "viem/chains"
import { type Chain } from "viem"
import { constants } from "helpers"

export type ChainConfig = {
  id: number
  name: string
  provider: "overtime" | "azuro" | "sx" | null
  currency: string
  icon: string
  betTokenIcon: string
}

export const getIsActive = (
  chain: Chain & ChainConfig,
  chainId: number,
  selectedTokenIndex: number,
  isSXChain: boolean,
  isOvertimeChain: boolean
) => {
  // First check if we're on the same chain
  if (chainId !== chain.id) {
    return false
  }

  // Special handling for Base chain
  if (chainId === base.id) {
    return chain.currency === (selectedTokenIndex === 0 ? "USDC" : "WETH")
  }

  // Special handling for Arbitrum chain
  if (chainId === arbitrum.id) {
    return (
      (chain.provider === "sx" && isSXChain) ||
      (chain.provider === "overtime" && isOvertimeChain)
    )
  }

  // For all other chains, just check if we're on the same chain
  return true
}

export const buildFilteredChains = (
  isAAWallet: boolean,
  availableChains: readonly Chain[],
  NODE_ENV: string
): (Chain & ChainConfig)[] => {
  const chainConfigs = isAAWallet
    ? constants.SOCIAL_LOGIN_CHAIN_CURRENCY_CONFIGS
    : constants.CUSTOM_CHAIN_CURRENCY_CONFIGS

  return availableChains.flatMap(chain =>
    chainConfigs
      .filter(
        config =>
          config.id === chain.id &&
          (!config.isTestnet || NODE_ENV === "development")
      )
      .map(config => ({ ...chain, ...config }))
  )
}

export const resolveCurrentChainConfig = (
  chainConfigs: ChainConfig[],
  chainId: number,
  selectedTokenIndex: number,
  isSXChain: boolean,
  isOvertimeChain: boolean
) => {
  let expectedCurrency: string | undefined
  let expectedProvider: string | undefined

  if (chainId === base.id) {
    expectedCurrency = selectedTokenIndex === 0 ? "USDC" : "WETH"
  } else if (chainId === arbitrum.id) {
    expectedCurrency = "USDC"
    expectedProvider = isSXChain
      ? "sx"
      : isOvertimeChain
        ? "overtime"
        : undefined
  }

  return chainConfigs.find(
    config =>
      config.id === chainId &&
      (!expectedCurrency || config.currency === expectedCurrency) &&
      (!expectedProvider || config.provider === expectedProvider)
  )
}

export const isArbitrumSXChain = (chain: Chain & ChainConfig) =>
  chain.id === arbitrum.id &&
  chain.currency === "USDC" &&
  chain.provider === "sx"
